import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { Dispatch } from 'redux'
import {
  submitPayment,
  getPayments,
  releasePayment,
  settlePayment,
  getReceivers,
  getSenders,
  getLockedPayments,
  changeFronting,
  lockPayment,
  changeReceivedPayments,
  rejectReceiver,
  queuePayment,
  startLoadingActivePayments,
  stopLoadingActivePayments,
  startLoadingLockedPayments,
  stopLoadingLockedPayments,
  getAllReceivers,
} from 'app/state/ducks/payments/actions'
import {
  PaymentParams,
  PaymentReceived,
  ReceiverParams,
} from 'app/models/payments-params'

export const loadLockedPayments = () => async (dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  console.log('loading locked payments')
  dispatch(startLoadingLockedPayments())
  return await client
    .get(`${apiServerUrl}/api/locked-payments`)
    .then(payments => {
      return dispatch(getLockedPayments(payments.data))
    })
    .catch(e => {
      console.log(e)
    })
    .finally(() => dispatch(stopLoadingLockedPayments()))
}

/**
 * The release queue's Payments grid in ONE request (ADR_LOS-030):
 * `GET /api/release-queue/payments` returns the queued, released and
 * rejected payments already tagged with their status — each in the shape its
 * state endpoint returned — plus the alert the page used to work out from
 * every receiver and entity in the tenant. The rows land in the same store,
 * in the same shape, so every in-place write reducer keeps working.
 *
 * Was: the three state endpoints here, plus `loadReceivers()` and
 * `loadEntities()` on the page, for the alert.
 */
export const loadActivePayments = () => async (dispatch: Dispatch) => {
  try {
    dispatch(startLoadingActivePayments())
    const apiServerUrl = config.backendUrl
    const response = await client.get(`${apiServerUrl}/api/release-queue/payments`)
    return dispatch(getPayments((response?.data as any[]) ?? []))
  } catch (e) {
    console.log(e)
    return []
  } finally {
    dispatch(stopLoadingActivePayments())
  }
}

export const submitPayments = (payment: any) => async (dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl

  return client
    .post(`${apiServerUrl}/api/queued-payments/submit`, {
      paymentId: { id: payment.id, admin: payment.payment.agencyAdmin },
    })
    .then(payment => {
      const payload = dispatch(submitPayment(payment.data))
      return { success: true, payload: payload.payload }
    })
    .catch(e => {
      console.log(e)
      return { success: false, payload: e.response.data.error }
    })
}

export const releasePayments = (payment: any) => async (dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  return client
    .post(`${apiServerUrl}/api/queued-payments/release`, {
      paymentId: { id: payment.id, admin: payment.payment.agencyAdmin },
    })
    .then(payment => {
      const payload = dispatch(releasePayment(payment.data))
      return { success: true, payload: payload.payload }
    })
    .catch(e => {
      return { success: false, payload: e.response.data.error }
    })
}

export const settlePayments = (payment: any) => async (dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  return client
    .post(`${apiServerUrl}/api/released-payments/settle`, {
      paymentId: { id: payment.id, admin: payment.payment.agencyAdmin },
    })
    .then(payment => {
      const payload = dispatch(settlePayment(payment.data))
      return { success: true, payload: payload.payload }
    })
    .catch(e => {
      return { success: false, payload: e.response.data.error }
    })
}

export const queuePayments = (payment: any) => async (dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  return client
    .post(`${apiServerUrl}/api/rejected-payments/queue`, {
      paymentId: { id: payment.id, admin: payment.payment.agencyAdmin },
    })
    .then(payment => {
      const payload = dispatch(queuePayment(payment.data))
      return { success: true, payload: payload.payload }
    })
    .catch(e => {
      return { success: false, payload: e.response.data.error }
    })
}

export const loadReceivers = (id?: string) => async (dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  if (!id) {
    return client
      .get(`${apiServerUrl}/api/payment-receivers`)
      .then(response => {
        dispatch(getAllReceivers(response.data))
        return { success: true, payload: response.data }
      })
      .catch(e => {
        console.log(e)
        return { success: false, payload: e.response.data.error }
      })
  }
  return client
    .get(`${apiServerUrl}/api/payment-receivers/payment?id=${id}`)
    .then(response => {
      dispatch(getReceivers(response.data))
      return { success: true, payload: response.data }
    })
    .catch(e => {
      console.log(e)
      return { success: false, payload: e.response.data.error }
    })
}

export const loadSenders = (id: string) => async (dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  return client
    .get(`${apiServerUrl}/api/payment-senders/payment?id=${id}`)
    .then(senders => {
      return dispatch(getSenders(senders.data))
    })
    .catch(e => {
      console.log(e)
      return []
    })
}

export const changeFrontingOnPayment =
  (payment: any, status: boolean) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/queued-payments/change-fronting`, {
        paymentId: { id: payment.id, admin: payment.payment.agencyAdmin },
        status: status,
      })
      .then(response => {
        payment.payment.fronting = status
        dispatch(changeFronting(payment))
        return { success: true, payload: response.data }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const rejectPayment =
  (receiver: ReceiverParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/payment-receivers/reject`, {
        paymentId: receiver.paymentId,
        lenderId: receiver.lenderId,
      })
      .then(response => {
        dispatch(
          rejectReceiver({
            rejected: response.data,
            editedId: receiver.paymentId,
          }),
        )
        return { success: true, payload: response.data }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const cancelPayment = (payment: any) => async (dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  const url =
    payment.status === 'Queued'
      ? `${apiServerUrl}/api/queued-payments/cancel`
      : `${apiServerUrl}/api/released-payments/cancel`
  return client
    .post(url, {
      paymentId: { id: payment.id, admin: payment.payment.agencyAdmin },
    })
    .then(response => {
      dispatch(lockPayment(payment))
      return { success: true, payload: response.data }
    })
    .catch(e => {
      return { success: false, payload: e.response.data.error }
    })
}

export const changeReceivedLenders =
  (payment: PaymentParams, paymentReceived: PaymentReceived[]) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/payment-senders/payment-received`, {
        paymentId: { id: payment.id, admin: payment.payment.agencyAdmin },
        paymentReceived: paymentReceived,
      })
      .then(response => {
        dispatch(changeReceivedPayments({ payment, paymentReceived }))
        return { success: true, payload: response.data }
      })
      .catch(e => {
        console.log(e, 'error')
        return { success: false, payload: e.response.data.error }
      })
  }
